import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Skip if returning from OAuth callback
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    if (res.data?.requires_2fa) {
      // Don't store anything yet — caller must complete the 2FA challenge
      return { requires_2fa: true, challenge_token: res.data.challenge_token };
    }
    localStorage.setItem("polla_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const completeLogin2FA = async (challengeToken, code) => {
    const res = await api.post("/auth/2fa/login", { challenge_token: challengeToken, code });
    localStorage.setItem("polla_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (email, password, nickname, inviteCode) => {
    const res = await api.post("/auth/register", { email, password, nickname, invite_code: inviteCode });
    localStorage.setItem("polla_token", res.data.token);
    setUser(res.data.user);
    return res.data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Logout endpoint may fail (network, expired token) — token is still
      // cleared client-side so the user is effectively logged out.
    }
    localStorage.removeItem("polla_token");
    setUser(null);
  };

  const updateNickname = async (nickname) => {
    const res = await api.patch("/auth/me", { nickname });
    setUser(res.data);
    return res.data;
  };

  // Stable context value so consumers don't re-render on every parent update.
  const value = useMemo(
    () => ({ user, loading, login, completeLogin2FA, register, logout, checkAuth, updateNickname }),
    [user, loading, checkAuth]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
